import {
  CLIENT_IMAGE_META_ANALYSIS_VERSION,
  type ClientImageMeta,
  type ClientImageMetaField,
} from '@/ai/image/client-meta';

const TRANSPARENCY_SCAN_TILE_EDGE = 512;
const CLIENT_IMAGE_META_TIMEOUT_MS = 2_500;
const CLIENT_IMAGE_META_PROBE_PARAM = 'client_meta_probe';
const fileAnalysisCache = new WeakMap<File, Promise<ClientImageMeta>>();
// URL inputs can change in place; keep reuse short-lived and memory bounded.
const urlAnalysisCache = new Map<
  string,
  { promise: Promise<ClientImageMeta>; expiresAt: number }
>();
const URL_ANALYSIS_CACHE_TTL_MS = 60_000;
const URL_ANALYSIS_CACHE_LIMIT = 32;

type ClientImageMetaRequirements = {
  fields: ReadonlyArray<ClientImageMetaField>;
};

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function resolveFormat(file: File): string {
  const mime = file.type.trim().toLowerCase();
  if (mime.startsWith('image/')) {
    const format = mime.slice('image/'.length);
    if (format === 'jpg') return 'jpeg';
    if (format.length > 0) return format;
  }

  const name = file.name.trim().toLowerCase();
  const extension = name.includes('.') ? name.split('.').pop() : '';
  if (extension === 'jpg') return 'jpeg';
  return extension || 'unknown';
}

function deriveFileNameFromUrl(url: string, contentType: string) {
  try {
    const pathname = new URL(url).pathname;
    const lastSegment = pathname.split('/').filter(Boolean).pop();
    if (lastSegment) {
      return decodeURIComponent(lastSegment);
    }
  } catch {
    // ignore invalid urls
  }

  const normalizedType = contentType.trim().toLowerCase();
  if (normalizedType === 'image/jpeg') return 'image.jpeg';
  if (normalizedType === 'image/png') return 'image.png';
  if (normalizedType === 'image/webp') return 'image.webp';
  if (normalizedType === 'image/gif') return 'image.gif';
  return 'image.bin';
}

export function buildClientImageMetaProbeUrl(url: string) {
  try {
    const target = new URL(url, window.location.href);
    if (target.protocol !== 'http:' && target.protocol !== 'https:') {
      return url;
    }
    if (!target.searchParams.has(CLIENT_IMAGE_META_PROBE_PARAM)) {
      target.searchParams.set(CLIENT_IMAGE_META_PROBE_PARAM, '1');
    }
    return target.toString();
  } catch {
    return url;
  }
}

function createAbortError(): DOMException {
  return new DOMException('Aborted', 'AbortError');
}

function withAbortAndTimeout<T>(
  promiseFactory: () => Promise<T>,
  params: { signal?: AbortSignal; timeoutMs: number }
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (timer) {
        clearTimeout(timer);
      }
      if (params.signal) {
        params.signal.removeEventListener('abort', onAbort);
      }
    };

    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      callback();
    };

    const onAbort = () => {
      finish(() => reject(createAbortError()));
    };

    if (params.signal?.aborted) {
      onAbort();
      return;
    }

    if (params.signal) {
      params.signal.addEventListener('abort', onAbort, { once: true });
    }

    timer = setTimeout(() => {
      finish(() => reject(new Error('client-image-meta-timeout')));
    }, params.timeoutMs);

    promiseFactory().then(
      (value) => finish(() => resolve(value)),
      (error) => finish(() => reject(error))
    );
  });
}

function loadImage(
  src: string,
  signal?: AbortSignal
): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';

    const cleanup = () => {
      image.onload = null;
      image.onerror = null;
      signal?.removeEventListener('abort', onAbort);
    };

    const onAbort = () => {
      cleanup();
      reject(createAbortError());
    };

    image.onload = () => {
      cleanup();
      resolve(image);
    };
    image.onerror = () => {
      cleanup();
      reject(new Error('client-image-meta-image-load-failed'));
    };

    if (signal?.aborted) {
      onAbort();
      return;
    }

    signal?.addEventListener('abort', onAbort, { once: true });
    image.src = src;
  });
}

export async function readClientImageDimensions(
  file: File,
  signal?: AbortSignal
) {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl, signal);
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    if (width <= 0 || height <= 0) {
      throw new Error('client-image-meta-invalid-natural-size');
    }
    return { width, height };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function hasTransparentPixels(data: Uint8ClampedArray): boolean {
  let alphaMin = 255;

  for (let index = 0; index < data.length; index += 4) {
    const alpha = data[index + 3] ?? 255;
    if (alpha < alphaMin) alphaMin = alpha;
    if (alphaMin < 255) return true;
  }

  return false;
}

function scanImageTransparency(params: {
  image: HTMLImageElement;
  width: number;
  height: number;
  signal?: AbortSignal;
}) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.min(params.width, TRANSPARENCY_SCAN_TILE_EDGE);
  canvas.height = Math.min(params.height, TRANSPARENCY_SCAN_TILE_EDGE);
  const context = canvas.getContext('2d', {
    willReadFrequently: true,
  });
  if (!context) {
    throw new Error('client-image-meta-no-canvas-context');
  }

  for (
    let sourceY = 0;
    sourceY < params.height;
    sourceY += TRANSPARENCY_SCAN_TILE_EDGE
  ) {
    const tileHeight = Math.min(
      TRANSPARENCY_SCAN_TILE_EDGE,
      params.height - sourceY
    );
    for (
      let sourceX = 0;
      sourceX < params.width;
      sourceX += TRANSPARENCY_SCAN_TILE_EDGE
    ) {
      if (params.signal?.aborted) {
        throw createAbortError();
      }

      const tileWidth = Math.min(
        TRANSPARENCY_SCAN_TILE_EDGE,
        params.width - sourceX
      );
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(
        params.image,
        sourceX,
        sourceY,
        tileWidth,
        tileHeight,
        0,
        0,
        tileWidth,
        tileHeight
      );
      if (
        hasTransparentPixels(
          context.getImageData(0, 0, tileWidth, tileHeight).data
        )
      ) {
        return true;
      }
    }
  }

  return false;
}

async function analyzeFile(
  file: File,
  signal?: AbortSignal
): Promise<ClientImageMeta> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl, signal);
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    if (width <= 0 || height <= 0) {
      throw new Error('client-image-meta-invalid-natural-size');
    }

    const transparentPixels = scanImageTransparency({
      image,
      width,
      height,
      signal,
    });

    return {
      analysisVersion: CLIENT_IMAGE_META_ANALYSIS_VERSION,
      width,
      height,
      format: resolveFormat(file),
      hasTransparentPixels: transparentPixels,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function downloadImageFile(
  url: string,
  signal?: AbortSignal
): Promise<File> {
  const response = await fetch(buildClientImageMetaProbeUrl(url), {
    signal,
    cache: 'force-cache',
  });
  if (!response.ok) {
    throw new Error(`client-image-meta-fetch-failed:${response.status}`);
  }

  const blob = await response.blob();
  const contentType =
    blob.type ||
    response.headers.get('content-type') ||
    'application/octet-stream';
  return new File([blob], deriveFileNameFromUrl(url, contentType), {
    type: contentType,
  });
}

function cacheUrlImageAnalysis(url: string, promise: Promise<ClientImageMeta>) {
  urlAnalysisCache.delete(url);
  urlAnalysisCache.set(url, {
    promise,
    expiresAt: Date.now() + URL_ANALYSIS_CACHE_TTL_MS,
  });
  if (urlAnalysisCache.size > URL_ANALYSIS_CACHE_LIMIT) {
    const oldest = urlAnalysisCache.keys().next().value;
    if (oldest !== undefined) urlAnalysisCache.delete(oldest);
  }
}

export function primeClientImageMeta(url: string, meta: ClientImageMeta) {
  const normalizedUrl = url.trim();
  if (!normalizedUrl) return;
  cacheUrlImageAnalysis(normalizedUrl, Promise.resolve(meta));
}

function getSharedImageAnalysis(file?: File | null, url?: string | null) {
  const normalizedUrl = url?.trim() || null;
  const cachedUrl = normalizedUrl
    ? urlAnalysisCache.get(normalizedUrl)
    : undefined;
  const cached = file
    ? fileAnalysisCache.get(file)
    : cachedUrl && cachedUrl.expiresAt > Date.now()
      ? cachedUrl.promise
      : undefined;
  if (cached) return cached;

  // Consumer cancellation must not cancel another consumer's shared analysis.
  const signal = AbortSignal.timeout(CLIENT_IMAGE_META_TIMEOUT_MS);
  const promise = withAbortAndTimeout(
    async () => {
      const source = file ?? (await downloadImageFile(normalizedUrl!, signal));
      return analyzeFile(source, signal);
    },
    { signal, timeoutMs: CLIENT_IMAGE_META_TIMEOUT_MS }
  ).catch((error: unknown) => {
    if (file && fileAnalysisCache.get(file) === promise) {
      fileAnalysisCache.delete(file);
    }
    if (
      !file &&
      normalizedUrl &&
      urlAnalysisCache.get(normalizedUrl)?.promise === promise
    ) {
      urlAnalysisCache.delete(normalizedUrl);
    }
    throw signal.aborted ? new Error('client-image-meta-timeout') : error;
  });
  if (file) {
    fileAnalysisCache.set(file, promise);
  } else if (normalizedUrl) {
    cacheUrlImageAnalysis(normalizedUrl, promise);
  }
  return promise;
}

export async function buildClientImageMeta(params: {
  toolId: string;
  file?: File | null;
  inputImageUrl?: string | null;
  requirements?: ClientImageMetaRequirements | null;
  signal?: AbortSignal;
}): Promise<ClientImageMeta | null> {
  const { toolId, file, inputImageUrl, signal } = params;
  const requirements = params.requirements;
  if (
    !requirements ||
    requirements.fields.length === 0 ||
    (!file && !inputImageUrl)
  ) {
    return null;
  }

  try {
    if (signal?.aborted) throw createAbortError();
    return await withAbortAndTimeout(
      () => getSharedImageAnalysis(file, inputImageUrl),
      {
        signal,
        timeoutMs: CLIENT_IMAGE_META_TIMEOUT_MS,
      }
    );
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    console.warn('[client_meta_analysis_failed]', {
      toolId,
      ...(file ? { fileName: file.name } : { inputImageUrl }),
      reason: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}
